import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import buttonImage from '../buttonimage.webp';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { checkReadingLimit, handleDeleteReading } from '../utils/readingUtils';

function Acknowledge({ goBack, goHome, handleSaveProgress }) {
    console.log('Debug: Acknowledge component rendered with props:', { goBack, goHome, handleSaveProgress });

    // Ensure the component renders regardless of current_question_id
    useEffect(() => {
        console.log('Debug: Acknowledge component mounted.');
    }, []);

    const [cards, setCards] = useState([]);
    const [pickerVisible, setPickerVisible] = useState(false);
    const [selectedCards, setSelectedCards] = useState([]);
    const [savedReadings, setSavedReadings] = useState([]);
    const [saving, setSaving] = useState(false);
    const [cardsLoaded, setCardsLoaded] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [clickedCardId, setClickedCardId] = useState(null);

    const retrySupabaseQuery = useCallback(async (queryFn, retries = 3, delay = 1000) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                return await queryFn();
            } catch (error) {
                if (attempt < retries) {
                    console.warn(`Retrying query (attempt ${attempt})...`);
                    await new Promise((resolve) => setTimeout(resolve, delay));
                } else {
                    throw error;
                }
            }
        }
    }, []);

    const fetchCards = useCallback(async () => {
        try {
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('cards')
                    .select('id, title, image_path, descriptions, card_number')
                    .eq('component', 'Concepts')
            );
            if (error) throw error;
            setCards(data || []);
            setCardsLoaded(true);
        } catch (err) {
            setCards([]);
            setCardsLoaded(false);
            toast.error('Failed to load cards.');
            console.error('Error fetching cards:', err.message);
        }
    }, [retrySupabaseQuery]);

    const fetchSavedReadings = useCallback(async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .select('*')
                    .eq('user_id', user.id)
                    .eq('component', 'Acknowledge')
                    .order('created_at', { ascending: false })
            );
            if (error) throw error;
            setSavedReadings(data || []);
        } catch (err) {
            console.error('Error fetching saved readings:', err.message);
        }
    }, [retrySupabaseQuery]);


    useEffect(() => {
        fetchCards();
        fetchSavedReadings();
    }, [fetchCards, fetchSavedReadings]);

    const handleCardClick = (card) => {
        if (selectedCards.length < 6 && !selectedCards.some(c => c.id === card.id)) {
            setSelectedCards([...selectedCards, card]);
            setClickedCardId(card.id);
            setTimeout(() => setClickedCardId(null), 200);
        }
    };

    const handleSaveReading = async () => {
        if (selectedCards.length !== 6) {
            toast.error('Please select exactly 6 cards to save your reading.');
            return;
        }

        setSaving(true);
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                toast.error('User not logged in. Please log in to save your reading.');
                return;
            }

            const canSave = await checkReadingLimit(user.id, 'Acknowledge', retrySupabaseQuery, supabase);
            if (!canSave) {
                toast.error('You have reached the limit of 10 saved readings for this picker. Delete a reading to save a new one.');
                setSaving(false);
                return;
            }

            const descriptions = selectedCards.reduce((acc, card, index) => {
                const position = `position${index + 1}`;
                acc[position] = card.descriptions?.Acknowledge?.[position] || card.descriptions || 'No description available';
                return acc;
            }, {});

            const readingData = {
                user_id: user.id,
                component: 'Acknowledge',
                cards: selectedCards.map((card) => card.id),
                descriptions,
            };

            const { error: insertError } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .insert(readingData)
            );

            if (insertError) {
                console.error('Supabase insert error:', insertError.message);
                throw insertError;
            }

            toast.success('Reading saved successfully!');
            setSelectedCards([]);
            setPickerVisible(false);
            await fetchSavedReadings();
        } catch (err) {
            toast.error('Failed to save reading. Please try again.');
            console.error('Error saving reading:', err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleLoadReading = (reading) => {
        if (!cardsLoaded || cards.length === 0) {
            toast.info('Loading card data. Please wait a moment and try again.');
            return;
        }

        const cardsById = cards.reduce((acc, card) => {
            acc[card.id] = card;
            return acc;
        }, {});

        const loadedCards = reading.cards.map((cardId) => {
            const card = cardsById[cardId];
            if (!card) {
                console.warn(`Card ID ${cardId} not found while loading saved reading.`);
                return null;
            }
            return {
                ...card,
                descriptions: card.descriptions || 'No description available',
            };
        }).filter(Boolean);

        if (loadedCards.length !== reading.cards.length) {
            toast.error('Some cards could not be loaded for this saved reading. Please refresh and try again.');
        }

        setSelectedCards(loadedCards);
        setPickerVisible(false);
    };

    // Shuffle helper
    const shuffleArray = (array) => {
        const arr = array.slice();
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    };

    const handleStartReading = () => {
        setSelectedCards([]);
        setPickerVisible((prev) => {
            const next = !prev;
            // Shuffle cards every time picker is shown
            if (!prev && cards.length > 0) setCards(shuffleArray([...cards]));
            return next;
        });
    };

    // Helper to filter out back images from card set
    const isNotBackImage = (card) => {
        if (!card.image_path) return true;
        return !card.image_path.toLowerCase().includes('essense back');
    };

    // Add this function to hide the card picker without clearing the reading display
    const handleHideCardPicker = () => {
        setPickerVisible(false);
    };

    return (
        <>
            <style>
                {`
                @media (max-width: 600px) {
                    .acknowledge-btn-row {
                        flex-direction: row !important;
                        gap: 6px !important;
                    }
                    .acknowledge-btn {
                        width: 32vw !important;
                        min-width: 0 !important;
                        max-width: 100px !important;
                        padding: 10px 0 !important;
                        font-size: 16px !important;
                    }
                    .acknowledge-card-picker {
                        display: flex !important;
                        flex-direction: column !important;
                        flex-wrap: nowrap !important;
                        overflow-x: visible !important;
                        max-width: 100vw !important;
                        padding-bottom: 8px !important;
                        align-items: flex-start !important;
                        margin-left: 0 !important;
                    }
                    .acknowledge-card-row {
                        display: flex !important;
                        flex-direction: row !important;
                        flex-wrap: nowrap !important;
                        margin-top: 0 !important;
                        margin-bottom: 8px !important;
                        width: 100vw !important;
                        min-width: 100vw !important;
                        justify-content: flex-start !important;
                        align-items: flex-end !important;
                        overflow-x: hidden !important;
                    }
                    .acknowledge-card {
                        width: 48px !important;
                        min-width: 48px !important;
                        height: 64px !important;
                        margin-left: -36px !important;
                        margin-right: 0 !important;
                        transition: transform 0.2s, box-shadow 0.2s;
                        z-index: 1;
                    }
                    .acknowledge-card:first-child {
                        margin-left: 0 !important;
                    }
                }
                `}
            </style>
            <div style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '16px',
            }}>
                <ToastContainer />
                {/* Title */}
                <h3 style={{
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: 'black',
                    marginBottom: '32px',
                }}>
                    Acknowledge
                </h3>

                {/* Top Card with Image, Title, and Subtitle */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '600px',
                        borderRadius: '10px',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        border: '2px solid black',
                        marginBottom: '24px',
                        overflow: 'hidden',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        textAlign: 'center',
                    }}
                >
                    <img
                        src={require('../mirrorimage.webp')}
                        alt="Mirror"
                        style={{
                            width: '100%',
                            borderRadius: '10px 10px 0 0',
                            objectFit: 'cover',
                            maxHeight: '220px',
                        }}
                    />
                    <div
                        style={{
                            padding: '24px',
                            background: 'rgba(255,255,255,0.85)',
                            borderBottomLeftRadius: '10px',
                            borderBottomRightRadius: '10px',
                        }}
                    >
                        <h2
                            style={{
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '28px',
                                fontWeight: 'bold',
                                color: '#4B5563',
                                margin: '0 0 16px 0',
                            }}
                        >
                            Acknowledge
                        </h2>
                        <p
                            style={{
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                color: '#4B5563',
                                margin: 0,
                            }}
                        >
                            The first step to change is to acknowledge what is present.
                        </p>
                    </div>
                </div>

                {/* Session Helper Cards Row */}
                <div
                    className="acknowledge-session-helper-row"
                    style={{
                        display: 'flex',
                        flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
                        gap: '16px',
                        width: '100%',
                        maxWidth: '600px',
                        marginBottom: '16px',
                    }}
                >
                    {/* Left Card */}
                    <div style={{
                        flex: 1,
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'left',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        marginBottom: window.innerWidth <= 600 ? '16px' : 0,
                    }}>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                            • Acknowledging what is in front of us is the first step.<br />
                            • We can acknowledge something in front of us even if it has not been part of a pattern.<br />
                            • Acknowledgement is like declaring our awareness of something which give it more of our attention for processing.<br />
                            • Whenever we acknowledge something is the right time for us to notice it.<br />
                            • Use the Oracle on this page for suggestions about what needs your Acknowledgment now.<br />
                            • If we have something bothering us but we do not know what it is exactly, the oracle can help us find a direction to look in.
                        </div>
                    </div>
                    {/* Right Card */}
                    <div style={{
                        flex: 1,
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'left',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                            1. Are you clear on what is in front of you to reflect on right now?<br />
                            2. Have you found yourself hesitant to acknowledge something uncomfortable in your experience?<br />
                            3. Do you have any thoughts looping that you can acknowledge now?<br />
                            4. Are there any feelings trying to get your attention now?<br />
                            5. Have you acknowledged any habits that are detrimental to you at this time?<br />
                            6. Can you discern when someone else is trying to get you to acknowledge something about yourself and it does not apply to you?
                        </div>
                    </div>
                </div>

                {/* Cards */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%',
                    maxWidth: '600px',
                }}>
                    {/* Card 1 */}
                    <div style={{
                        width: '100%',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'center',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}>
                        <p style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            color: 'black',
                        }}>
                            We can not change anything that we do not acknowledge.  That is why acknowledge is the first step in the formula I offer for freeing ourselves from repeated unwanted experiences, thoughts, feelings, or behaviors.  The human mind is designed to recognize patterns.  Often when we are ready to heal from something unwanted, we will notice the pattern of it.  This might be something we have experienced for years prior to noticing the pattern.  It is when we acknowledge that as a pattern that we want to alleviate that we enter the process of releasing the loop of energy. Some of the natural coping mechanisms that humans use are denial, supression, and projection.  These are some examples of ways we can keep ourselves from acknowledging what is in front of us. Whenever something is ready to be resolved in our experience we may notice the pattern get louder with more instances or bigger examples of it.  When something has been long ignored it can be amplified by correlating physical ailments. Louise Hayes has a book about the correlating metaphysical meanings of ailments.  This is not official diagnostic criteria and I am not giving advice in a professional capacity, but I can share that I have used the book to give me hints about what I needed to acknowledge in order to alleviate my discomforts in the past.  Though it is not required that we dig for root causes when we do this work for ourselves, I have often done so with great results.   
                        </p>
                    </div>

                    {/* Card 2 */}
                    <div style={{
                        width: '100%',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'center',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}>
                        <p style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            color: 'black',
                        }}>
                            When something is ready to be self healed we will acknowledge it.  It will be in front of us and we will notice that something is off, or there is a repeating discomfort that we seek to alleviate.

                        </p>
                    </div>

                    {/* Card 3 */}
                    <div style={{
                        width: '100%',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'center',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}>
                        <p style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            color: 'black',
                        }}>
                            Sometimes we know something is off, or we know we are stuck experiencing a looping pattern that we want to alleviate, but we do not quite see it clearly. Maybe it is difficult to fully acknowledge. If we are in the habit of justifying something or over looking it we might fear that we could easily fall back into the pattern. The Oracle on this page is here to offer information to reflect on.  Use your own intuition to interpret and reflect on what cards come out for your reading.  You can save up to 10 readings at a time for further reflection.  The Oracle is simply a reflective tool that assists you to see your own thoughts and feelings in response to the cards you get.  This can be quite helpful when we are faced with looking at what is in front of us.
                        </p>
                    </div>
                    {/* New Card with Invitation meme4 image */}
                    <div style={{
                        width: '100%',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'center',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        marginBottom: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <span
                            style={{
                                fontFamily: 'Times New Roman, Times, serif',
                                fontWeight: 'bold',
                                fontSize: '32px',
                                color: '#374151',
                                lineHeight: 1.2,
                                display: 'block',
                                margin: '12px 0',
                                textAlign: 'center',
                            }}
                        >
                            I am happy to invite all humans who are ready to adopt the understanding that there is a solution for every challenge.  Humans are not victims, and we are capable of evolving through all of the challenges we face,  if that possibility is acknowledged, and understood.
                        </span>
                    </div>
                </div>

                {/* Start Card Reading Button */}
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: 'auto',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        margin: '16px auto',
                    }}
                    onClick={handleStartReading}
                    aria-describedby="card-picker-sr-notice"
                >
                    {pickerVisible ? 'Hide Card Picker' : 'Start Card Reading'}
                </button>

                {/* Card Picker */}
                {pickerVisible && (
                    <div
                        className="acknowledge-card-picker"
                        style={{
                            width: window.innerWidth <= 768 ? '100vw' : '1000px',
                            maxWidth: window.innerWidth <= 768 ? '100vw' : '1000px',
                            margin: '0 auto',
                            padding: '10px 0',
                            background: 'white',
                            border: '2px solid #4B5563',
                            borderRadius: '8px',
                            zIndex: 1,
                            overflow: 'hidden',
                            position: 'relative',
                            boxShadow: 'none',
                        }}
                    >
                        <div style={{
                            display: 'block',
                            verticalAlign: 'top',
                            position: 'relative',
                            padding: '0',
                            textAlign: 'left',
                            overflow: 'visible',
                            background: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                            marginLeft: '6px',
                            marginRight: '6px',
                        }}>
                            {(() => {
                                // Only show the back image for each card in the picker
                                const conceptsBackImage = "https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/card-images/Concepts/cbackimage.webp";
                                const filteredCards = cards.filter(card =>
                                    !card.image_path?.toLowerCase().includes('cbackimage.webp')
                                );
                                const totalCards = filteredCards.length;
                                const cardWidth = 150;
                                const cardHeight = 150;
                                const margin = 6;
                                const numRows = window.innerWidth <= 768 ? 10 : 5;
                                const rowGap = 8;
                                const containerWidth = (window.innerWidth <= 768 ? window.innerWidth : 1000) - 2 * margin;
                                const cardsPerRow = Math.ceil(totalCards / numRows);
                                const overlapPx = cardsPerRow > 1
                                    ? Math.floor((cardWidth * cardsPerRow - containerWidth) / (cardsPerRow - 1))
                                    : 0;
                                const effectiveOverlap = overlapPx > 0 ? overlapPx : 0;
                                // Dynamically create rows
                                const rows = [];
                                for (let i = 0; i < totalCards; i++) {
                                    const rowIdx = Math.floor(i / cardsPerRow);
                                    if (!rows[rowIdx]) rows[rowIdx] = [];
                                    rows[rowIdx].push(filteredCards[i]);
                                }
                                const containerHeight = numRows * cardHeight + (numRows - 1) * rowGap;
                                return (
                                    <div
                                        style={{
                                            width: `${containerWidth}px`,
                                            minWidth: '300px',
                                            position: 'relative',
                                            height: `${containerHeight}px`,
                                            background: 'transparent',
                                            border: 'none',
                                            boxShadow: 'none',
                                        }}
                                    >
                                        {rows.map((rowCards, rowIdx) => (
                                            <div
                                                key={`concepts-row-${rowIdx}`}
                                                style={{
                                                    position: 'absolute',
                                                    top: `${rowIdx * (cardHeight + rowGap)}px`,
                                                    left: 0,
                                                    height: `${cardHeight}px`,
                                                    width: `${containerWidth}px`,
                                                    minWidth: '300px',
                                                    overflowX: 'visible',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    boxShadow: 'none',
                                                    marginBottom: rowIdx < numRows - 1 ? `${rowGap}px` : '0',
                                                }}
                                            >
                                                {rowCards.map((card, idx) => {
                                                    const left = idx * (cardWidth - effectiveOverlap);
                                                    const maxLeft = containerWidth - cardWidth;
                                                    const adjustedLeft = Math.min(left, maxLeft);
                                                    const zIndex = idx;
                                                    const key = card?.id ? `concepts-row-${rowIdx}-${card.id}` : `concepts-row-${rowIdx}-${idx}`;
                                                    return (
                                                        <div
                                                            key={key}
                                                            data-position={idx}
                                                            style={{
                                                                width: `${cardWidth}px`,
                                                                height: `${cardHeight}px`,
                                                                position: 'absolute',
                                                                left: `${adjustedLeft}px`,
                                                                top: '0px',
                                                                cursor: 'pointer',
                                                                borderRadius: '4px',
                                                                boxShadow: clickedCardId === card.id ? '0 0 12px rgba(34,197,94,0.6)' : 'none',
                                                                zIndex: zIndex,
                                                                transition: 'transform 0.3s, opacity 0.3s',
                                                                background: 'transparent',
                                                                border: 'none',
                                                                ...(idx === rowCards.length - 1 ? { zIndex: 100 } : {}),
                                                                opacity: selectedCards.some(c => c.id === card.id) ? 0.5 : 1,
                                                                transform: clickedCardId === card.id ? 'scale(1.06)' : 'scale(1)',
                                                            }}
                                                            onClick={() => handleCardClick(card)}
                                                        >
                                                            <img
                                                                src={conceptsBackImage}
                                                                alt="Concepts Card Back"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'contain',
                                                                    borderRadius: '4px',
                                                                    pointerEvents: 'none',
                                                                    userSelect: 'none',
                                                                    display: 'block',
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    boxShadow: 'none',
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}

                {/* Selected Cards Display */}
                {selectedCards.filter(isNotBackImage).length > 0 && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gridTemplateRows: 'repeat(2, 1fr)',
                        gap: '8px',
                        marginTop: '16px',
                        background: 'white',
                        border: '2px solid black',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        padding: '12px',
                        borderRadius: '8px',
                        maxWidth: '600px',
                        margin: '16px auto',
                        width: '100%',
                    }}>
                        {selectedCards.filter(isNotBackImage).map((card, index) => {
                            const labels = [
                                'Current state of the situation',
                                'How is your unconscious motivating the/contributing to the situation',
                                'Present challenge',
                                'Hidden Benefit',
                                'Opportunity ',
                                'What needs to be seen'
                            ];
                            return (
                                <div key={card.id} style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.3), 0 -4px 6px rgba(0,0,0,0.3)',
                                }}>
                                    <img
                                        key={card.id}
                                        src={
                                            card.image_path
                                                ? `https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/${card.image_path.replace('card-images/concepts/', 'card-images/Concepts/')}`
                                                : 'https://via.placeholder.com/150/000000/FFFFFF?text=NO+IMG'
                                        }
                                        alt={card.title || 'Card'}
                                        style={{ width: '150px', height: '150px', objectFit: 'contain' }}
                                        onError={(e) => {
                                            e.target.src = 'https://via.placeholder.com/150/000000/FFFFFF?text=NO+IMG';
                                        }}
                                    />
                                    <p style={{
                                        fontFamily: 'Times New Roman, Times, serif',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        marginTop: '8px',
                                        textAlign: 'center',
                                        wordWrap: 'break-word',
                                        maxWidth: '150px',
                                    }}>
                                        {labels[index] || 'No description available'}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Start Card Reading Button and Hide Card Picker Button - stacked vertically above Save Reading */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                    margin: '16px auto'
                }}>
                    <button
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: 'auto',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={handleStartReading}
                        disabled={cards.length === 0}
                    >
                        Start Card Reading
                    </button>
                    {pickerVisible && (
                        <button
                            style={{
                                backgroundImage: `url(${buttonImage})`,
                                backgroundSize: 'cover',
                                border: '2px solid black',
                                borderRadius: '10px',
                                padding: '10px 20px',
                                width: 'auto',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            }}
                            onClick={handleHideCardPicker}
                            aria-describedby="card-picker-sr-notice"
                        >
                            Hide Card Picker
                        </button>
                    )}
                </div>

                {/* Save Reading Button */}
                {selectedCards.length === 6 && (
                    <button
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: 'auto',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            margin: '16px auto',
                        }}
                        onClick={handleSaveReading}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Reading'}
                    </button>
                )}

                {/* Navigation Buttons */}
                <div
                    className="acknowledge-btn-row"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '16px',
                        marginTop: '32px',
                    }}>
                    <button
                        className="acknowledge-btn"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '120px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={goBack}
                    >Back</button>
                    <button
                        className="acknowledge-btn"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '120px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={goHome}
                    >Home</button>
                    <button
                        className="acknowledge-btn"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '120px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={() => handleSaveProgress('acknowledge')} // Save as camel case
                    >Save</button>
                </div>

                {/* Saved Readings */}
                {savedReadings.length > 0 && (
                    <div style={{
                        marginTop: '32px',
                        width: '100%',
                        maxWidth: '600px',
                        padding: '8px',
                    }}>
                        <h4 style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            marginBottom: '16px',
                        }}>
                            Saved Readings
                        </h4>
                        {savedReadings.map((reading) => (
                            <div
                                key={reading.id}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px',
                                    border: '1px solid black',
                                    borderRadius: '8px',
                                    marginBottom: '8px',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: '14px',
                                }}
                            >
                                <span onClick={() => handleLoadReading(reading)}>
                                    {new Date(reading.created_at).toLocaleString()}
                            </span>
                            <span
                                style={{
                                    color: 'red',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteReading(reading.id, fetchSavedReadings, retrySupabaseQuery, supabase);
                                }}
                            >
                                -delete
                            </span>
                        </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}

export default Acknowledge;